import { useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { AppButton, SelectInput } from "@/components/ui/primitives";
import { CollapsibleSection } from "@/components/ds";
import { DomainSidebar, DomainSidebarHeader, DomainTwoColumnContent, DomainTwoColumnLayout } from "@/components/ui/DomainSidebar";
import { useStandardPeriodRange } from "@/hooks/useStandardPeriodRange";
import {
  useCategories,
  useCategoryColors,
  useCategoryIcons,
  useCategoriesBalance,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "@/hooks/useCategories";
import { formatSignedCurrency } from "@/utils/formatters";
import { NewCategoryModal } from "../components/NewCategoryModal";
import { CategoryTransactionsModal } from "../components/CategoryTransactionsModal";
import { confirmAction, showAppMessage } from "@/components/ui/confirmDialog";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { useNavigationState } from "@/hooks/useNavigationState";
import { DomainChatTrigger } from "@/domains/chat/components/DomainChatTrigger";
import { SearchInput } from "@/components/ui/SearchInput";
import { hasCategoryActivity } from "@/domains/categories/utils/categoryPresentation";
import { CategoryCard, CategoryCardAdd } from "../components/CategoryCard";
import "../categories.css";

interface CategoryRow {
  id: number;
  name: string;
  description?: string;
  icon_id?: number | null;
  color_id?: number | null;
  category_total_expense: number;
  category_total_deposit: number;
  percentage_of_total_expense: number;
  percentage_of_total_deposit: number;
}

interface CategoryDetails {
  id: number;
  name: string;
  periodBalance: number;
}

type CategorySort = "expense-desc" | "expense-asc" | "name-asc" | "name-desc";

const formatExpenseMoney = (value: number) => formatSignedCurrency(-Math.abs(value));

export const CategoriesView = () => {
  const { t } = useTranslation(["common", "categories", "accounts", "dashboard"]);
  const { selectedPeriod, setSelectedPeriod, customStart, setCustomStart, customEnd, setCustomEnd, selectedRange } =
    useStandardPeriodRange("this-month", "categories");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useNavigationState("categories.searchTerm", "");
  const [sortBy, setSortBy] = useNavigationState<CategorySort>("categories.sortBy", "expense-desc");
  const [showInactive, setShowInactive] = useNavigationState("categories.showInactive", false);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: number;
    name: string;
    description?: string;
  icon_id?: number | null;
  color_id?: number | null;
  } | null>(null);
  const [detailsCategory, setDetailsCategory] = useState<CategoryDetails | null>(null);


  const {
    data: categoryBalanceData,
    isLoading: loadingCategoryBalance,
    error: categoryBalanceError,
  } = useCategoriesBalance({
    billable_date_gte: selectedRange.start,
    billable_date_lte: selectedRange.end,
    category_total_expense: "desc",
  });
  const { data: categories = [], isLoading: loadingCategories, error: categoriesError } = useCategories();
  const createCategoryMutation = useCreateCategory();
  const updateCategoryMutation = useUpdateCategory();
  const deleteCategoryMutation = useDeleteCategory();

  const { data: categoryColors = [] } = useCategoryColors();
  const { data: categoryIcons = [] } = useCategoryIcons();
  const iconById = new Map(categoryIcons.map((icon) => [icon.id, icon]));
  const colorById = new Map(categoryColors.map((color) => [color.id, color]));

  const totalExpense = Number(categoryBalanceData?.total_expense || 0);

  const enrichedCategories = useMemo(() => {
    const categoryBalance = categoryBalanceData?.categories_balance ?? [];
    const categoryMap = new Map(categories.map((category) => [category.id, category]));
    const balanceMap = new Map(categoryBalance.map((category) => [category.id, category]));

    const availableCategories = categories.map((category) => {
      const balance = balanceMap.get(category.id);
      return {
        id: category.id,
        name: category.name,
        description: category.description ?? balance?.description,
        color_id: category.color_id ?? null,
        icon_id: category.icon_id ?? null,
        category_total_expense: Number(balance?.category_total_expense ?? 0),
        category_total_deposit: Number(balance?.category_total_deposit ?? 0),
        percentage_of_total_expense: Number(balance?.percentage_of_total_expense ?? 0),
        percentage_of_total_deposit: Number(balance?.percentage_of_total_deposit ?? 0),
      };
    });
    return [...availableCategories, ...categoryBalance.filter((category) => !categoryMap.has(category.id))];
  }, [categoryBalanceData?.categories_balance, categories]);
  const filteredCategories = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase();
    if (!query) return enrichedCategories;

    return enrichedCategories.filter((category) =>
      [category.name, category.description].some((value) =>
        value?.toLocaleLowerCase().includes(query),
      ),
    );
  }, [enrichedCategories, searchTerm]);

  const expenseCategories = useMemo(
    () =>
      enrichedCategories
        .filter((category) => Number(category.category_total_expense ?? 0) > 0)
        .sort((a, b) => Number(b.category_total_expense ?? 0) - Number(a.category_total_expense ?? 0)),
    [enrichedCategories],
  );
  const { activeManagementCategories, inactiveManagementCategories } = useMemo(() => {
    const compare = (first: CategoryRow, second: CategoryRow) => {
      if (sortBy === "name-asc" || sortBy === "name-desc") {
        const difference = first.name.localeCompare(second.name, undefined, { sensitivity: "base", numeric: true });
        return sortBy === "name-desc" ? -difference : difference;
      }
      const difference = Number(first.category_total_expense ?? 0) - Number(second.category_total_expense ?? 0);
      return sortBy === "expense-asc" ? difference : -difference;
    };
    const sorted = [...filteredCategories].sort(compare);
    return {
      activeManagementCategories: sorted.filter(hasCategoryActivity),
      inactiveManagementCategories: sorted.filter((category) => !hasCategoryActivity(category)),
    };
  }, [filteredCategories, sortBy]);


  const handleCreateCategory = async (
    categoryData: { name: string; description?: string; icon_id?: number | null; color_id?: number | null },
    categoryId?: number,
  ) => {
    if (categoryId) {
      await updateCategoryMutation.mutateAsync({ id: categoryId, data: categoryData });
    } else {
      await createCategoryMutation.mutateAsync(categoryData);
    }
    setSelectedCategory(null);
    setIsModalOpen(false);
  };

  const handleEditCategory = (category: { id: number; name: string; description?: string; icon_id?: number | null; color_id?: number | null }) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const openCategoryEditor = (category: CategoryRow) => {
    handleEditCategory({
      id: category.id,
      name: category.name,
      description: category.description,
      color_id: category.color_id,
      icon_id: category.icon_id,
    });
  };

  const handleDeleteCategory = async (categoryId: number) => {
    const confirmed = await confirmAction({
      title: t("common.prompts.deleteCategory"),
      body: t("common.prompts.cannotUndo", { defaultValue: "This action cannot be undone." }),
      confirmLabel: t("common.actions.delete", { defaultValue: "Delete" }),
      cancelLabel: t("common.actions.cancel", { defaultValue: "Cancel" }),
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteCategoryMutation.mutateAsync(categoryId);
    } catch {
      showAppMessage({ title: t("common.errors.deleteCategory"), tone: "danger" });
    }
  };

  const handleCategoryClick = (category: CategoryRow) => {
    setDetailsCategory({
      id: category.id,
      name: category.name,
      periodBalance: Number(category.category_total_deposit ?? 0) - Number(category.category_total_expense ?? 0),
    });
  };

  const handleEditCategoryFromList = (category: CategoryRow) => {
    openCategoryEditor(category);
  };

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCategory(null);
  };

  const renderCategoryCard = (category: CategoryRow) => {
    const expense = Number(category.category_total_expense ?? 0);
    const deposit = Number(category.category_total_deposit ?? 0);
    // Categorías de ingreso: muestran lo depositado y su parte del ingreso, no un gasto en $0.
    const isIncome = deposit > expense;
    const share = Number((isIncome ? category.percentage_of_total_deposit : category.percentage_of_total_expense) ?? 0);

    return (
      <CategoryCard
        key={category.id}
        wide={category.id === expenseCategories[0]?.id}
        featuredLabel={t("categories.highestSpending", { defaultValue: "Más gasto" })}
        name={category.name}
        icon={category.icon_id == null ? null : iconById.get(category.icon_id)?.name}
        color={category.color_id == null ? null : colorById.get(category.color_id) ?? null}
        noMovementLabel={t("categories.inactiveTitle")}
        amount={isIncome ? formatSignedCurrency(deposit) : formatExpenseMoney(expense)}
        hasMovement={hasCategoryActivity(category)}
        share={share}
        shareLabel={t(isIncome ? "categories.ofIncome" : "categories.ofSpending")}
        actions={{ edit: t("categories:editCategory"), view: t("categories:viewMovements", { defaultValue: "Ver movimientos" }), delete: t("categories:deleteCategory"), menu: t("categories.showActions", { defaultValue: "Mostrar acciones de categoría" }) }}
        onOpen={() => handleCategoryClick(category)}
        onEdit={() => handleEditCategoryFromList(category)}
        onView={() => setDetailsCategory({ id: category.id, name: category.name, periodBalance: deposit - expense })}
        onDelete={async () => { await handleDeleteCategory(category.id); }}
      />
    );
  };

  if (loadingCategoryBalance || loadingCategories) {
    return <PageSkeleton lines={5} />;
  }

  if (categoryBalanceError || categoriesError) {
    return (
      <div className="app-page flex items-center justify-center">
        <div className="app-surface border-[color:color-mix(in_srgb,var(--app-danger)_22%,var(--app-border)_78%)] bg-[color:color-mix(in_srgb,var(--app-danger)_8%,var(--app-surface)_92%)] px-8 py-6 text-center">
          <p className="font-medium text-[var(--app-text)]">{t("categories.loadError")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page categories-page">
      <div className="app-container max-w-none overflow-x-hidden">
        <div className="relative mx-auto w-full max-w-2xl min-w-0 overflow-x-hidden md:max-w-none">
          <DomainTwoColumnLayout className="categories-layout">
            <DomainSidebar>
            <DomainSidebarHeader greeting={t("common:labels.greeting")} title={t("common:nav.categories")} action={<DomainChatTrigger domain="categories" />} />

            {(
              <section className="app-domain-sidebar-section">
                <h2 className="app-domain-sidebar-label">
                  {t("categories.distributionTitle")}
                </h2>
                <p className="app-domain-sidebar-value text-[var(--app-danger)]">
                  {formatExpenseMoney(totalExpense)}
                </p>
              </section>
            )}

            </DomainSidebar>

            <DomainTwoColumnContent className="categories-content">
            {categories.length === 0 ? (
              <section className="pb-6">
                <div className="app-panel-raised px-5 py-6">
                  <p className="text-base text-[var(--app-text-secondary)]">{t("categories.emptyStateBody")}</p>
                  <AppButton
                    type="button"
                    tone="primary"
                    shape="pill"
                    className="mt-5"
                    onClick={handleAddCategory}
                    leadingIcon={<FiPlus className="h-4 w-4" />}
                  >
                    {t("categories.addFirstAllocation")}
                  </AppButton>
                </div>
              </section>
            ) : (
              <>
                <div role="toolbar" aria-label={t("common.labels.categories")} className="categories-toolbar">
                  <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder={`${t("common.actions.search")} ${t("common.labels.category")}`} label={`${t("common.actions.search")} ${t("common.labels.category")}`} />
                  <label className="sr-only" htmlFor="category-cycle">{t("common.labels.cycle")}</label>
                  <SelectInput id="category-cycle" value={selectedPeriod ?? "this-month"} onChange={(event) => setSelectedPeriod(event.target.value as "this-month" | "last-month" | "custom")} aria-label={t("common.labels.cycle")}>
                    <option value="this-month">{t("common.labels.cycle")}: {t("common.periods.thisMonth")}</option>
                    <option value="last-month">{t("common.labels.cycle")}: {t("common.periods.lastMonth")}</option>
                    <option value="custom">{t("common.labels.cycle")}: {t("common.periods.custom")}</option>
                  </SelectInput>
                  <label className="sr-only" htmlFor="category-sort">{t("accounts.sortByLabel")}</label>
                  <SelectInput id="category-sort" value={sortBy} onChange={(event) => setSortBy(event.target.value as CategorySort)} aria-label={t("accounts.sortByLabel")}>
                    <option value="expense-desc">{t("accounts.sortByAmount")} ↓</option>
                    <option value="expense-asc">{t("accounts.sortByAmount")} ↑</option>
                    <option value="name-asc">{t("accounts.sortByName")} A–Z</option>
                    <option value="name-desc">{t("accounts.sortByName")} Z–A</option>
                  </SelectInput>
                  {selectedPeriod === "custom" ? (
                    <div className="app-custom-cycle-panel grid grid-cols-2 gap-3 md:col-span-3">
                      <label className="app-domain-sidebar-control-label"><span className="app-domain-sidebar-label">{t("common.labels.startDate")}</span><input type="date" value={customStart} max={customEnd} onChange={(event) => setCustomStart(event.target.value)} className="app-domain-sidebar-date-input" /></label>
                      <label className="app-domain-sidebar-control-label"><span className="app-domain-sidebar-label">{t("common.labels.endDate")}</span><input type="date" value={customEnd} min={customStart} onChange={(event) => setCustomEnd(event.target.value)} className="app-domain-sidebar-date-input" /></label>
                    </div>
                  ) : null}
                </div>
                {filteredCategories.length === 0 ? (
                  <p className="py-8 text-center text-sm text-[var(--ds-ink-muted)]">{t("common.messages.noResults", { defaultValue: "No se encontraron resultados." })}</p>
                ) : null}
                {activeManagementCategories.length > 0 ? (
                <section className="pb-8">
                  <div className="mb-6 flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-[1.125rem] font-semibold text-[var(--app-text)]">{t("categories.listTitle")}</h2>
                      <p className="mt-2 text-sm text-[var(--app-text-secondary)]">
                        {t("categories.countLabel", { count: activeManagementCategories.length })}
                      </p>
                    </div>
                  </div>
                  <div className="categories-grid">
                    {activeManagementCategories.map(renderCategoryCard)}
                  </div>

                </section>
                ) : null}
                {inactiveManagementCategories.length > 0 ? (
                <div className="pb-8">
                  <CollapsibleSection
                    title={t("categories.inactiveTitle")}
                    subtitle={t("categories.countLabel", { count: inactiveManagementCategories.length })}
                    open={showInactive}
                    onOpenChange={setShowInactive}
                    toggleLabel={(open) =>
                      t(open ? "dashboard.collapseSection" : "dashboard.expandSection", { section: t("categories.inactiveTitle") })
                    }
                  >
                    <div className="categories-grid">
                      {inactiveManagementCategories.map(renderCategoryCard)}
                    </div>
                  </CollapsibleSection>
                </div>
                ) : null}
                <section className="categories-create">
                  <div className="categories-grid">
                    <CategoryCardAdd onClick={handleAddCategory} label={t("categories.createCard", { defaultValue: "Crear categoría" })} hint={t("categories.createCardHint", { defaultValue: "Organiza tus movimientos" })} />
                  </div>
                </section>
              </>
            )}
            </DomainTwoColumnContent>
          </DomainTwoColumnLayout>
        </div>

        <NewCategoryModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleCreateCategory}
          category={selectedCategory}
        />
        {detailsCategory ? (
          <CategoryTransactionsModal
            categoryId={detailsCategory.id}
            categoryName={detailsCategory.name}
            periodBalance={detailsCategory.periodBalance}
            start={selectedRange.start}
            end={selectedRange.end}
            onClose={() => setDetailsCategory(null)}
          />
        ) : null}
      </div>
    </div>
  );
};
